import React, { useEffect, useState, Fragment } from 'react';
/**
 * We are still seeing unexpected token errors in jest tests which could be
 * due to @babel problems that haven't been resolved or implemented
 */
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { styled as styled2, cssMap } from '@compiled/react';

import { N90, N100 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';

import { useTaskRowsContext } from './TaskRowsContext';
import type { TruncatedTextCacheKeys } from './TaskRowsContext';
import { HeightAdjustedContainer } from './HeightAdjustedContainer';
import { useRefHeight } from './useRefHeight';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled(HeightAdjustedContainer)({
	width: '100%',
	height: '100%',
	position: 'relative',

	// Match the description container margin.
	marginTop: '2px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContainerCompiled = styled2(HeightAdjustedContainer)({
	width: '100%',
	height: '100%',
	position: 'relative',

	// Match the description container margin.
	marginTop: '2px',
});

// This is absolutely positioned because we want to calculate the size of the text
// without affecting the DOM and visibility is used instead of display because
// visibility will still take up the space for calculations whereas display will remove it entirely.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const TextContainer = styled.div<{ isVisible: boolean }>(({ isVisible }) => ({
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	visibility: isVisible ? 'visible' : 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> a': {
		color: token('color.text', N100),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		':hover, :visited': {
			color: token('color.text', N90),
		},
	},
}));

const textContainerStyles = cssMap({
	default: {
		position: 'absolute',
		visibility: 'hidden',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'> a': {
			color: token('color.text', N100),
			'&:hover, &:visited': {
				color: token('color.text', N90),
			},
		},
	},
	visible: {
		visibility: 'visible',
	},
});

interface HeightAdjustingTextProps {
	defaultText: string; // The default text to truncate.
	taskId: string;
	cacheKey: TruncatedTextCacheKeys; // Used to fetch cached value from context or store truncated value into context.
	url?: string;
}

export const HeightAdjustingText = ({
	defaultText,
	taskId,
	cacheKey,
	url,
}: HeightAdjustingTextProps) => {
	const { height, ref } = useRefHeight();
	const [isLoading, setIsLoading] = useState(true);
	const { taskRows, setTaskRow } = useTaskRowsContext();

	const currentTask = taskRows[taskId];
	const taskHeight = currentTask?.rowHeight ?? 0;
	const cacheValue = currentTask?.[cacheKey];

	// Set the truncated page as state to prevent recalculation
	const [truncatedText, setTruncatedText] = useState<string>(cacheValue ?? defaultText);
	const [isVisible, setIsVisible] = useState<boolean>(false);

	useEffect(() => {
		setIsLoading(true);
		setIsVisible(false);
		setTruncatedText(cacheValue ?? defaultText);
	}, [cacheValue, defaultText]);

	useEffect(() => {
		// Must use the node here because node.clientHeight is always read in real time.
		// Whereas the height from useRefHeight only calculates the inital height when ref is defined.
		if (isLoading && Number.isInteger(height) && Number.isInteger(taskHeight)) {
			// The container height is bigger than the taskHeight so it is overflowing. Adjust.
			if (height > taskHeight && truncatedText.length > 0) {
				setTruncatedText((text) => text.substring(0, text.length - 1));
			} else {
				setIsVisible(true);
				setIsLoading(false);
				setTaskRow(taskId, { [cacheKey]: truncatedText });
			}
		}
	}, [
		height,
		taskHeight,
		truncatedText,
		setTruncatedText,
		setTaskRow,
		taskId,
		cacheKey,
		isVisible,
		isLoading,
		defaultText,
	]);

	useEffect(() => {
		setTaskRow(taskId, { isLoading });
	}, [isLoading, setTaskRow, taskId]);

	const pageTitle = (
		<Fragment>
			{truncatedText}
			{truncatedText.length < defaultText.length ? '...' : null}
		</Fragment>
	);

	if (fg('confluence_frontend_home_compiled_migration')) {
		return (
			<ContainerCompiled taskId={taskId}>
				<div
					css={[textContainerStyles['default'], isVisible && textContainerStyles['visible']]}
					ref={ref}
				>
					{url ? (
						<a target="_blank" href={url}>
							{pageTitle}
						</a>
					) : (
						pageTitle
					)}
				</div>
			</ContainerCompiled>
		);
	}

	return (
		<Container taskId={taskId}>
			<TextContainer ref={ref} isVisible={isVisible}>
				{url ? (
					<a target="_blank" href={url}>
						{pageTitle}
					</a>
				) : (
					pageTitle
				)}
			</TextContainer>
		</Container>
	);
};
